import React, { useEffect, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { ChevronLeftIcon, CalendarIcon, UsersIcon, CheckCircleIcon, SparklesIcon, ClockIcon, MapPinIcon, PhoneIcon, MailIcon, UtensilsIcon, AlertCircleIcon, CreditCardIcon, ShieldCheckIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
// Mock menu data
const menuForDate = {
  starters: ['Premium Medjool Dates with Almonds', 'Fresh Fruit Platter', 'Traditional Harira Soup', 'Lentil Soup with Lemon', 'Vegetable Samosas', 'Chicken Spring Rolls'],
  mainCourse: ['Lamb Biryani with Raita', 'Grilled Hammour Fish', 'Chicken Tikka Masala', 'Beef Kofta', 'Vegetable Korma', 'Butter Chicken', 'Prawn Curry', 'Mixed Grill Platter'],
  desserts: ['Kunafa with Pistachio', 'Baklava Assortment', 'Traditional Falooda', 'Umm Ali', 'Maldivian Bondi', 'Chocolate Mousse'],
  shortEats: ['Bajiya (Fish Pastry)', 'Keemia (Fish Rolls)', 'Kulhi Boakibaa', 'Masroshi', 'Gulha (Fish Balls)', 'Hedhikaa Mix'],
  beverages: ['Fresh Laban', 'Jallab', 'Fresh Juices', 'Mint Lemonade', 'Arabic Coffee', 'Karak Chai']
};
const dietaryOptions = ['No Restrictions', 'Vegetarian', 'Vegan', 'Gluten-Free', 'Dairy-Free', 'Nut Allergy', 'Seafood Allergy', 'Halal Only'];
const formatDateDisplay = (dateStr: string) => {
  const [year, month, day] = dateStr.split('-');
  return `${day}/${month}/${year}`;
};
export function Booking() {
  const {
    id
  } = useParams();
  const [searchParams] = useSearchParams();
  // Get pre-filled data from URL params
  const prefilledType = searchParams.get('type') as 'regular' | 'promotional';
  const prefilledDate = searchParams.get('date');
  const prefilledPax = searchParams.get('pax');
  const prefilledTime = searchParams.get('time');
  // Determine initial step based on whether data is pre-filled
  const [step, setStep] = useState(prefilledDate && prefilledPax && prefilledTime ? 2 : 1);
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
  const [availabilityChecked, setAvailabilityChecked] = useState(prefilledDate && prefilledPax && prefilledTime ? true : false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<{
    code: string;
    discount: number;
    type: 'percentage' | 'fixed';
  } | null>(null);
  const [couponError, setCouponError] = useState('');
  const [bookingType, setBookingType] = useState<'regular' | 'promotional'>(prefilledType || 'promotional');
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string>(prefilledTime || '');
  const [availableTimeSlots, setAvailableTimeSlots] = useState<Array<{
    time: string;
    available: number;
  }>>([]);
  // Card details state for regular bookings
  const [cardDetails, setCardDetails] = useState({
    cardNumber: '',
    cardName: '',
    expiryDate: '',
    cvv: ''
  });
  // Set default date to tomorrow or use pre-filled date
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const defaultDate = prefilledDate || tomorrow.toISOString().split('T')[0];
  const [formData, setFormData] = useState({
    date: defaultDate,
    pax: prefilledPax ? parseInt(prefilledPax) : 2,
    name: '',
    phone: '',
    email: '',
    company: '',
    dietaryRequirements: [] as string[],
    specialRequests: ''
  });
  // Initialize time slots if data is pre-filled
  useEffect(() => {
    if (prefilledDate && prefilledPax && prefilledTime) {
      const slots = generateTimeSlots(bookingType);
      setAvailableTimeSlots(slots);
    }
  }, []);
  // Generate timeslots based on booking type
  const generateTimeSlots = (type: 'regular' | 'promotional') => {
    if (type === 'promotional') {
      // Iftar specific timeslots (limited around breaking fast time)
      return [{
        time: '18:00',
        available: 8
      }, {
        time: '18:15',
        available: 12
      }, {
        time: '18:30',
        available: 15
      }, {
        time: '18:45',
        available: 10
      }, {
        time: '19:00',
        available: 6
      }];
    } else {
      // Regular dining timeslots (throughout the day)
      return [{
        time: '12:00',
        available: 10
      }, {
        time: '12:30',
        available: 8
      }, {
        time: '13:00',
        available: 12
      }, {
        time: '13:30',
        available: 9
      }, {
        time: '14:00',
        available: 7
      }, {
        time: '18:00',
        available: 15
      }, {
        time: '18:30',
        available: 14
      }, {
        time: '19:00',
        available: 16
      }, {
        time: '19:30',
        available: 12
      }, {
        time: '20:00',
        available: 10
      }, {
        time: '20:30',
        available: 8
      }, {
        time: '21:00',
        available: 6
      }];
    }
  };
  const pricePerPax = bookingType === 'promotional' ? 450 : 350;
  const gst = 0.08;
  const serviceCharge = 0.1;
  const subtotal = formData.pax * pricePerPax;
  const gstAmount = subtotal * gst;
  const serviceChargeAmount = subtotal * serviceCharge;
  const totalBeforeDiscount = subtotal + gstAmount + serviceChargeAmount;
  // Calculate discount
  let discountAmount = 0;
  if (appliedCoupon) {
    if (appliedCoupon.type === 'percentage') {
      discountAmount = totalBeforeDiscount * (appliedCoupon.discount / 100);
    } else {
      discountAmount = appliedCoupon.discount;
    }
  }
  const total = totalBeforeDiscount - discountAmount;
  // Mock coupon codes
  const validCoupons = {
    RAMADAN20: {
      discount: 20,
      type: 'percentage' as const
    },
    IFTAR50: {
      discount: 50,
      type: 'fixed' as const
    },
    WELCOME15: {
      discount: 15,
      type: 'percentage' as const
    }
  };
  const applyCoupon = () => {
    const upperCode = couponCode.toUpperCase();
    if (validCoupons[upperCode as keyof typeof validCoupons]) {
      setAppliedCoupon({
        code: upperCode,
        ...validCoupons[upperCode as keyof typeof validCoupons]
      });
      setCouponError('');
    } else {
      setCouponError('Invalid coupon code');
      setAppliedCoupon(null);
    }
  };
  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponError('');
  };
  const checkAvailability = () => {
    setIsCheckingAvailability(true);
    setTimeout(() => {
      const slots = generateTimeSlots(bookingType);
      setAvailableTimeSlots(slots);
      setIsCheckingAvailability(false);
      setAvailabilityChecked(true);
      setSelectedTimeSlot(''); // Reset selected time when checking new availability
    }, 1500);
  };
  const handleDietaryToggle = (option: string) => {
    setFormData(prev => {
      const current = prev.dietaryRequirements;
      if (current.includes(option)) {
        return {
          ...prev,
          dietaryRequirements: current.filter(item => item !== option)
        };
      } else {
        return {
          ...prev,
          dietaryRequirements: [...current, option]
        };
      }
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (step === 1 && !selectedTimeSlot) {
      return; // Don't proceed if no timeslot selected
    }
    if (step === 3 && !acceptedTerms) {
      return;
    }
    if (step < 3) {
      setStep(step + 1);
    } else {
      setStep(4);
    }
  };
  if (step === 4) {
    return <div className="w-full min-h-screen" data-id="element-167">
        <Header data-id="element-168" />
        <div className="max-w-3xl mx-auto px-4 py-16" data-id="element-169">
          <div className="bg-white rounded-3xl shadow-2xl p-12 text-center relative overflow-hidden" data-id="element-170">
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-full -mr-32 -mt-32 opacity-50" data-id="element-171"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-teal-100 to-cyan-100 rounded-full -ml-24 -mb-24 opacity-50" data-id="element-172"></div>
            <div className="relative z-10" data-id="element-173">
              <div className="mb-6 inline-block" data-id="element-174">
                <div className="bg-gradient-to-br from-green-400 to-emerald-500 rounded-full p-6 animate-bounce" data-id="element-175">
                  <CheckCircleIcon className="w-16 h-16 text-white" data-id="element-176" />
                </div>
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-3" data-id="element-177">
                Booking Confirmed! 🎉
              </h1>
              <p className="text-gray-600 text-lg mb-8 max-w-md mx-auto" data-id="element-178">
                Your {bookingType === 'promotional' ? 'Iftar' : 'table'}{' '}
                reservation has been successfully confirmed. We have sent a
                confirmation email to {formData.email}
              </p>
              <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-8 mb-8 text-left border-2 border-cyan-200" data-id="element-179">
                <div className="flex items-center gap-3 mb-6" data-id="element-180">
                  <SparklesIcon className="w-6 h-6 text-cyan-600" data-id="element-181" />
                  <h3 className="text-xl font-bold text-gray-900" data-id="element-182">
                    Booking Details
                  </h3>
                </div>
                <div className="space-y-4" data-id="element-183">
                  <div className="flex items-start gap-4" data-id="element-184">
                    <div className="bg-white rounded-xl p-3" data-id="element-185">
                      <UtensilsIcon className="w-5 h-5 text-cyan-600" data-id="element-186" />
                    </div>
                    <div data-id="element-187">
                      <p className="text-sm text-gray-600" data-id="element-188">Booking Type</p>
                      <p className="font-bold text-gray-900 text-lg" data-id="element-189">
                        {bookingType === 'promotional' ? 'Ramadan Iftar Special (Set Menu)' : 'Regular Table Booking'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4" data-id="element-190">
                    <div className="bg-white rounded-xl p-3" data-id="element-191">
                      <CalendarIcon className="w-5 h-5 text-cyan-600" data-id="element-192" />
                    </div>
                    <div data-id="element-193">
                      <p className="text-sm text-gray-600" data-id="element-194">Date</p>
                      <p className="font-bold text-gray-900 text-lg" data-id="element-195">
                        {formData.date}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4" data-id="element-196">
                    <div className="bg-white rounded-xl p-3" data-id="element-197">
                      <UsersIcon className="w-5 h-5 text-cyan-600" data-id="element-198" />
                    </div>
                    <div data-id="element-199">
                      <p className="text-sm text-gray-600" data-id="element-200">Party Size</p>
                      <p className="font-bold text-gray-900 text-lg" data-id="element-201">
                        {formData.pax} guests
                      </p>
                    </div>
                  </div>
                  {selectedTimeSlot && <div className="flex items-start gap-4" data-id="element-202">
                      <div className="bg-white rounded-xl p-3" data-id="element-203">
                        <ClockIcon className="w-5 h-5 text-cyan-600" data-id="element-204" />
                      </div>
                      <div data-id="element-205">
                        <p className="text-sm text-gray-600" data-id="element-206">Time</p>
                        <p className="font-bold text-gray-900 text-lg" data-id="element-207">
                          {selectedTimeSlot}
                        </p>
                      </div>
                    </div>}
                  <div className="pt-4 border-t-2 border-cyan-200" data-id="element-208">
                    <div className="flex justify-between items-center" data-id="element-209">
                      <span className="text-gray-600 font-medium" data-id="element-210">
                        Total Amount Paid
                      </span>
                      <span className="text-2xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent" data-id="element-211">
                        MVR {total.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              {bookingType === 'promotional' && <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-2xl p-6 mb-8" data-id="element-212">
                  <p className="text-purple-800 font-semibold flex items-center justify-center gap-2" data-id="element-213">
                    <SparklesIcon className="w-5 h-5" data-id="element-214" />
                    You have been entered into our daily lucky draw!
                  </p>
                </div>}
              <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-215">
                <Link to="/" data-id="element-216">
                  <button className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white px-8 py-4 rounded-xl font-semibold transition-all transform hover:scale-105 shadow-lg" data-id="element-217">
                    Back to Home
                  </button>
                </Link>
                <button className="border-2 border-gray-300 hover:border-cyan-500 text-gray-700 hover:text-cyan-600 px-8 py-4 rounded-xl font-semibold transition-all" data-id="element-218">
                  View My Bookings
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>;
  }
  return <div className="w-full min-h-screen" data-id="element-219">
      <Header data-id="element-220" />
      <div className="max-w-7xl mx-auto px-4 py-8" data-id="element-221">
        <Link to={`/restaurant/${id}`} className="inline-flex items-center gap-2 text-red-600 hover:text-red-700 mb-8 font-semibold transition-colors" data-id="element-222">
          <ChevronLeftIcon className="w-5 h-5" data-id="element-223" />
          <span data-id="element-224">Back to restaurant</span>
        </Link>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-225">
          {/* Main Booking Form */}
          <div className="lg:col-span-2" data-id="element-226">
            <div className="bg-white rounded-3xl shadow-xl p-8" data-id="element-227">
              <h1 className="text-4xl font-bold bg-gradient-to-r from-red-600 to-red-700 bg-clip-text text-transparent mb-8" data-id="element-228">
                Complete Your Booking
              </h1>
              {/* Booking Type Selection */}
              <div className="mb-8" data-id="element-229">
                <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-230">
                  Select Booking Type
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-231">
                  <div onClick={() => setBookingType('regular')} className={`border-3 rounded-2xl p-6 cursor-pointer transition-all duration-300 transform hover:scale-105 ${bookingType === 'regular' ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-cyan-50 shadow-xl scale-105' : 'border-gray-200 hover:border-blue-300 bg-white'}`} data-id="element-232">
                    <div className="flex items-center gap-3 mb-3" data-id="element-233">
                      <div className={`p-3 rounded-xl ${bookingType === 'regular' ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-234">
                        <UtensilsIcon className={`w-6 h-6 ${bookingType === 'regular' ? 'text-white' : 'text-gray-500'}`} data-id="element-235" />
                      </div>
                      <h3 className="font-bold text-gray-900 text-lg" data-id="element-236">
                        Regular Table Booking
                      </h3>
                    </div>
                    <p className="text-gray-600 text-sm mb-2" data-id="element-237">
                      À la carte dining from our regular menu
                    </p>
                    <p className="text-sm font-semibold text-gray-700" data-id="element-238">
                      No prepayment required • Pay at restaurant
                    </p>
                  </div>
                  <div onClick={() => setBookingType('promotional')} className={`border-3 rounded-2xl p-6 cursor-pointer transition-all duration-300 transform hover:scale-105 relative overflow-hidden ${bookingType === 'promotional' ? 'border-red-500 bg-gradient-to-br from-red-50 to-orange-50 shadow-xl scale-105' : 'border-gray-200 hover:border-red-300 bg-white'}`} data-id="element-239">
                    <div className="absolute top-2 right-2" data-id="element-240">
                      <span className="bg-gradient-to-r from-red-600 to-red-700 text-white text-xs font-bold px-3 py-1 rounded-full" data-id="element-241">
                        Special Offer
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mb-3" data-id="element-242">
                      <div className={`p-3 rounded-xl ${bookingType === 'promotional' ? 'bg-red-600' : 'bg-gray-200'}`} data-id="element-243">
                        <SparklesIcon className={`w-6 h-6 ${bookingType === 'promotional' ? 'text-white' : 'text-gray-500'}`} data-id="element-244" />
                      </div>
                      <h3 className="font-bold text-gray-900 text-lg" data-id="element-245">
                        Ramadan Iftar Special
                      </h3>
                    </div>
                    <p className="text-gray-600 text-sm mb-2" data-id="element-246">
                      Premium buffet with traditional Iftar dishes
                    </p>
                    <p className="text-sm font-semibold text-gray-700" data-id="element-247">
                      MVR 450 per person • Prepayment required
                    </p>
                  </div>
                </div>
              </div>
              {/* Progress Steps */}
              <div className="mb-10" data-id="element-248">
                <div className="flex items-center justify-between w-full" data-id="element-249">
                  {[{
                  num: 1,
                  label: 'Date & Availability'
                }, {
                  num: 2,
                  label: 'Your Details'
                }, {
                  num: 3,
                  label: bookingType === 'promotional' ? 'Payment' : 'Card Guarantee'
                }].map((s, idx, arr) => <div key={s.num} className="flex items-center flex-1" data-id="element-250">
                      <div className="flex flex-col items-center flex-1" data-id="element-251">
                        <div className={`flex items-center justify-center w-12 h-12 rounded-full font-bold transition-all duration-300 ${step >= s.num ? 'bg-gradient-to-br from-red-600 to-red-700 text-white shadow-lg scale-110' : 'bg-gray-200 text-gray-500'}`} data-id="element-252">
                          {step > s.num ? <CheckCircleIcon className="w-6 h-6" data-id="element-253" /> : s.num}
                        </div>
                        <span className={`text-xs mt-2 font-semibold text-center ${step >= s.num ? 'text-red-600' : 'text-gray-400'}`} data-id="element-254">
                          {s.label}
                        </span>
                      </div>
                      {idx < arr.length - 1 && <div className={`flex-1 h-1 mx-2 rounded-full transition-all duration-300 ${step > s.num ? 'bg-gradient-to-r from-red-600 to-red-700' : 'bg-gray-200'}`} data-id="element-255" />}
                    </div>)}
                </div>
              </div>
              <form onSubmit={handleSubmit} data-id="element-256">
                {/* Step 1: Date & Availability */}
                {step === 1 && <div className="space-y-6 animate-fadeIn" data-id="element-257">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-258">
                      Check Availability
                    </h2>
                    <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-6 border-2 border-red-200" data-id="element-259">
                      <label className="block text-sm font-bold text-gray-900 mb-3 flex items-center gap-2" data-id="element-260">
                        <CalendarIcon className="w-5 h-5 text-red-600" data-id="element-261" />
                        Select Date
                      </label>
                      <input type="date" required value={formData.date} onChange={e => {
                    setFormData({
                      ...formData,
                      date: e.target.value
                    });
                    setAvailabilityChecked(false);
                  }} min={new Date().toISOString().split('T')[0]} className="w-full px-6 py-4 border-2 border-red-300 rounded-xl text-gray-900 focus:outline-none focus:ring-4 focus:ring-red-200 focus:border-red-500 transition-all text-lg font-semibold" data-id="element-262" />
                    </div>
                    <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-6 border-2 border-orange-200" data-id="element-263">
                      <label className="block text-sm font-bold text-gray-900 mb-3 flex items-center gap-2" data-id="element-264">
                        <UsersIcon className="w-5 h-5 text-orange-600" data-id="element-265" />
                        Number of Guests
                      </label>
                      <select required value={formData.pax} onChange={e => {
                    setFormData({
                      ...formData,
                      pax: parseInt(e.target.value)
                    });
                    setAvailabilityChecked(false);
                  }} className="w-full px-6 py-4 border-2 border-orange-300 rounded-xl text-gray-900 focus:outline-none focus:ring-4 focus:ring-orange-200 focus:border-orange-500 transition-all text-lg font-semibold cursor-pointer" data-id="element-266">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => <option key={n} value={n} data-id="element-267">
                            {n} {n === 1 ? 'Guest' : 'Guests'}
                          </option>)}
                      </select>
                    </div>
                    <button type="button" onClick={checkAvailability} disabled={isCheckingAvailability} className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-6 py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-268">
                      {isCheckingAvailability ? <span className="flex items-center justify-center gap-2" data-id="element-269">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" data-id="element-270"></div>
                          Checking Availability...
                        </span> : 'Check Availability'}
                    </button>
                    {availabilityChecked && <div className="space-y-4" data-id="element-271">
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-2xl p-6 animate-fadeIn" data-id="element-272">
                          <div className="flex items-start gap-3 mb-4" data-id="element-273">
                            <CheckCircleIcon className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" data-id="element-274" />
                            <div data-id="element-275">
                              <p className="font-bold text-green-900 mb-1" data-id="element-276">
                                Tables Available!
                              </p>
                              <p className="text-sm text-green-800" data-id="element-277">
                                Great news! We have availability for{' '}
                                {formData.pax} guests on {formData.date}
                              </p>
                            </div>
                          </div>
                        </div>
                        {/* Timeslot Selection */}
                        <div className="bg-white rounded-2xl p-6 border-2 border-gray-200" data-id="element-278">
                          <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-279">
                            <ClockIcon className="w-5 h-5 text-blue-600" data-id="element-280" />
                            Select Your Preferred Time
                          </h3>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-id="element-281">
                            {availableTimeSlots.map(slot => <button key={slot.time} type="button" onClick={() => setSelectedTimeSlot(slot.time)} className={`p-4 rounded-xl border-2 transition-all transform hover:scale-105 ${selectedTimeSlot === slot.time ? 'border-blue-600 bg-gradient-to-br from-blue-50 to-cyan-50 shadow-lg' : 'border-gray-200 hover:border-blue-300 bg-white'}`} data-id="element-282">
                                <p className={`font-bold text-lg mb-1 ${selectedTimeSlot === slot.time ? 'text-blue-600' : 'text-gray-900'}`} data-id="element-283">
                                  {slot.time}
                                </p>
                                <p className="text-xs text-gray-600" data-id="element-284">
                                  {slot.available} tables
                                </p>
                              </button>)}
                          </div>
                          {!selectedTimeSlot && <p className="text-sm text-red-600 mt-3 flex items-center gap-2" data-id="element-285">
                              <AlertCircleIcon className="w-4 h-4" data-id="element-286" />
                              Please select a time to continue
                            </p>}
                        </div>
                        {bookingType === 'promotional' && selectedTimeSlot && <div className="bg-white rounded-xl p-4 border-2 border-gray-200" data-id="element-287">
                            <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2" data-id="element-288">
                              <UtensilsIcon className="w-5 h-5 text-red-600" data-id="element-289" />
                              Today's Iftar Menu Preview
                            </h4>
                            <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-290">
                              <div data-id="element-291">
                                <p className="font-semibold text-gray-700 mb-1" data-id="element-292">
                                  Starters
                                </p>
                                <ul className="text-gray-600 space-y-1" data-id="element-293">
                                  {menuForDate.starters.slice(0, 2).map((item, idx) => <li key={idx} data-id="element-294">• {item}</li>)}
                                </ul>
                              </div>
                              <div data-id="element-295">
                                <p className="font-semibold text-gray-700 mb-1" data-id="element-296">
                                  Main Course
                                </p>
                                <ul className="text-gray-600 space-y-1" data-id="element-297">
                                  {menuForDate.mainCourse.slice(0, 2).map((item, idx) => <li key={idx} data-id="element-298">• {item}</li>)}
                                </ul>
                              </div>
                            </div>
                            <p className="text-xs text-gray-500 mt-2" data-id="element-299">
                              + Desserts & Short Eats
                            </p>
                          </div>}
                        {bookingType === 'promotional' && selectedTimeSlot && <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-300 rounded-2xl p-5" data-id="element-300">
                            <div className="flex items-start gap-3" data-id="element-301">
                              <ClockIcon className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" data-id="element-302" />
                              <div data-id="element-303">
                                <p className="font-bold text-amber-900 mb-1" data-id="element-304">
                                  Important Timing
                                </p>
                                <p className="text-sm text-amber-800" data-id="element-305">
                                  Please arrive 10-15 minutes before your
                                  selected time ({selectedTimeSlot}) to ensure
                                  you are seated comfortably.
                                </p>
                              </div>
                            </div>
                          </div>}
                      </div>}
                  </div>}
                {/* Step 2: Dietary Requirements & Details */}
                {step === 2 && <div className="space-y-6 animate-fadeIn" data-id="element-306">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-307">
                      Your Information & Preferences
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-308">
                      <div data-id="element-309">
                        <label className="block text-sm font-bold text-gray-900 mb-2" data-id="element-310">
                          Full Name *
                        </label>
                        <input type="text" required value={formData.name} onChange={e => setFormData({
                      ...formData,
                      name: e.target.value
                    })} className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-4 focus:ring-red-200 focus:border-red-500 transition-all" placeholder="Enter your full name" data-id="element-311" />
                      </div>
                      <div data-id="element-312">
                        <label className="block text-sm font-bold text-gray-900 mb-2 flex items-center gap-2" data-id="element-313">
                          <PhoneIcon className="w-4 h-4 text-red-600" data-id="element-314" />
                          Phone Number *
                        </label>
                        <input type="tel" required value={formData.phone} onChange={e => setFormData({
                      ...formData,
                      phone: e.target.value
                    })} className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-4 focus:ring-red-200 focus:border-red-500 transition-all" placeholder="+960 XXX-XXXX" data-id="element-315" />
                      </div>
                      <div data-id="element-316">
                        <label className="block text-sm font-bold text-gray-900 mb-2 flex items-center gap-2" data-id="element-317">
                          <MailIcon className="w-4 h-4 text-red-600" data-id="element-318" />
                          Email Address *
                        </label>
                        <input type="email" required value={formData.email} onChange={e => setFormData({
                      ...formData,
                      email: e.target.value
                    })} className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-4 focus:ring-red-200 focus:border-red-500 transition-all" placeholder="your@email.com" data-id="element-319" />
                      </div>
                      <div data-id="element-320">
                        <label className="block text-sm font-bold text-gray-900 mb-2" data-id="element-321">
                          Company (Optional)
                        </label>
                        <input type="text" value={formData.company} onChange={e => setFormData({
                      ...formData,
                      company: e.target.value
                    })} className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-4 focus:ring-red-200 focus:border-red-500 transition-all" placeholder="Company name" data-id="element-322" />
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border-2 border-green-200" data-id="element-323">
                      <label className="block text-sm font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-324">
                        <UtensilsIcon className="w-5 h-5 text-green-600" data-id="element-325" />
                        Dietary Requirements
                      </label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-id="element-326">
                        {dietaryOptions.map(option => <button key={option} type="button" onClick={() => handleDietaryToggle(option)} className={`px-4 py-3 rounded-xl font-semibold text-sm transition-all transform hover:scale-105 ${formData.dietaryRequirements.includes(option) ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-lg' : 'bg-white text-gray-700 border-2 border-gray-300 hover:border-green-500'}`} data-id="element-327">
                            {option}
                          </button>)}
                      </div>
                    </div>
                    <div data-id="element-328">
                      <label className="block text-sm font-bold text-gray-900 mb-2" data-id="element-329">
                        Special Requests (Optional)
                      </label>
                      <textarea value={formData.specialRequests} onChange={e => setFormData({
                    ...formData,
                    specialRequests: e.target.value
                  })} rows={4} className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-4 focus:ring-red-200 focus:border-red-500 transition-all resize-none" placeholder="Any additional requests or allergies we should know about..." data-id="element-330" />
                    </div>
                  </div>}
                {/* Step 3: Payment or Card Guarantee */}
                {step === 3 && <div className="space-y-6 animate-fadeIn" data-id="element-331">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-332">
                      {bookingType === 'promotional' ? 'Review & Payment' : 'Card Guarantee'}
                    </h2>
                    <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 border-2 border-blue-200" data-id="element-333">
                      <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-334">
                        <CheckCircleIcon className="w-5 h-5 text-blue-600" data-id="element-335" />
                        Booking Summary
                      </h3>
                      <div className="space-y-3 text-sm" data-id="element-336">
                        <div className="flex justify-between" data-id="element-337">
                          <span className="text-gray-600" data-id="element-338">Booking Type</span>
                          <span className="font-semibold text-gray-900" data-id="element-339">
                            {bookingType === 'promotional' ? 'Ramadan Iftar Special' : 'Regular Table'}
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-340">
                          <span className="text-gray-600" data-id="element-341">Date</span>
                          <span className="font-semibold text-gray-900" data-id="element-342">
                            {formData.date}
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-343">
                          <span className="text-gray-600" data-id="element-344">Guests</span>
                          <span className="font-semibold text-gray-900" data-id="element-345">
                            {formData.pax} people
                          </span>
                        </div>
                        {selectedTimeSlot && <div className="flex justify-between" data-id="element-346">
                            <span className="text-gray-600" data-id="element-347">Time</span>
                            <span className="font-semibold text-gray-900" data-id="element-348">
                              {selectedTimeSlot}
                            </span>
                          </div>}
                        {formData.dietaryRequirements.length > 0 && <div className="flex justify-between items-start" data-id="element-349">
                            <span className="text-gray-600" data-id="element-350">Dietary</span>
                            <span className="font-semibold text-gray-900 text-right" data-id="element-351">
                              {formData.dietaryRequirements.join(', ')}
                            </span>
                          </div>}
                      </div>
                    </div>
                    {bookingType === 'regular' ? <>
                        {/* Card Guarantee Section for Regular Bookings */}
                        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-6 border-2 border-amber-300" data-id="element-352">
                          <div className="flex items-start gap-3 mb-4" data-id="element-353">
                            <AlertCircleIcon className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" data-id="element-354" />
                            <div data-id="element-355">
                              <p className="font-bold text-amber-900 mb-1" data-id="element-356">
                                Card Guarantee Required
                              </p>
                              <p className="text-sm text-amber-800" data-id="element-357">
                                To secure your reservation, we require a valid
                                card on file. Your card will NOT be charged now.
                                It will only be used if you fail to show up or
                                cancel within 24 hours of your reservation.
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="bg-white rounded-2xl p-6 border-2 border-gray-200" data-id="element-358">
                          <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-359">
                            <CreditCardIcon className="w-5 h-5 text-gray-700" data-id="element-360" />
                            Card Information
                          </h3>
                          <div className="space-y-4" data-id="element-361">
                            <div data-id="element-362">
                              <label className="block text-sm font-bold text-gray-900 mb-2" data-id="element-363">
                                Card Number *
                              </label>
                              <input type="text" required value={cardDetails.cardNumber} onChange={e => {
                          const value = e.target.value.replace(/\s/g, '');
                          if (value.length <= 16 && /^\d*$/.test(value)) {
                            const formatted = value.match(/.{1,4}/g)?.join(' ') || value;
                            setCardDetails({
                              ...cardDetails,
                              cardNumber: formatted
                            });
                          }
                        }} placeholder="1234 5678 9012 3456" className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-200 focus:border-blue-500 transition-all" data-id="element-364" />
                            </div>
                            <div data-id="element-365">
                              <label className="block text-sm font-bold text-gray-900 mb-2" data-id="element-366">
                                Cardholder Name *
                              </label>
                              <input type="text" required value={cardDetails.cardName} onChange={e => setCardDetails({
                          ...cardDetails,
                          cardName: e.target.value
                        })} placeholder="Name on card" className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-200 focus:border-blue-500 transition-all" data-id="element-367" />
                            </div>
                            <div className="grid grid-cols-2 gap-4" data-id="element-368">
                              <div data-id="element-369">
                                <label className="block text-sm font-bold text-gray-900 mb-2" data-id="element-370">
                                  Expiry Date *
                                </label>
                                <input type="text" required value={cardDetails.expiryDate} onChange={e => {
                            let value = e.target.value.replace(/\D/g, '');
                            if (value.length >= 2) {
                              value = value.slice(0, 2) + '/' + value.slice(2, 4);
                            }
                            if (value.length <= 5) {
                              setCardDetails({
                                ...cardDetails,
                                expiryDate: value
                              });
                            }
                          }} placeholder="MM/YY" className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-200 focus:border-blue-500 transition-all" data-id="element-371" />
                              </div>
                              <div data-id="element-372">
                                <label className="block text-sm font-bold text-gray-900 mb-2" data-id="element-373">
                                  CVV *
                                </label>
                                <input type="text" required value={cardDetails.cvv} onChange={e => {
                            const value = e.target.value.replace(/\D/g, '');
                            if (value.length <= 4) {
                              setCardDetails({
                                ...cardDetails,
                                cvv: value
                              });
                            }
                          }} placeholder="123" className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-200 focus:border-blue-500 transition-all" data-id="element-374" />
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border-2 border-green-200" data-id="element-375">
                          <div className="flex items-start gap-3" data-id="element-376">
                            <ShieldCheckIcon className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" data-id="element-377" />
                            <div data-id="element-378">
                              <p className="font-bold text-green-900 mb-1" data-id="element-379">
                                Secure & Safe
                              </p>
                              <p className="text-sm text-green-800" data-id="element-380">
                                Your card information is encrypted and secure.
                                We partner with Bank of Maldives for payment
                                processing.
                              </p>
                            </div>
                          </div>
                        </div>
                      </> : <>
                        {/* Existing payment section for promotional bookings */}
                        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-6 border-2 border-gray-300" data-id="element-381">
                          <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-382">
                            <AlertCircleIcon className="w-5 h-5 text-gray-700" data-id="element-383" />
                            Terms & Conditions
                          </h3>
                          <div className="bg-white rounded-xl p-4 mb-4 max-h-48 overflow-y-auto text-sm text-gray-700 space-y-2" data-id="element-384">
                            <p data-id="element-385">
                              <strong data-id="element-386">1. Booking Confirmation:</strong> Your
                              booking is confirmed upon successful payment.
                            </p>
                            <p data-id="element-387">
                              <strong data-id="element-388">2. Cancellation Policy:</strong>{' '}
                              Cancellations made 24+ hours before reservation
                              receive full refund. Within 24 hours:
                              non-refundable.
                            </p>
                            {bookingType === 'promotional' && <p data-id="element-389">
                                <strong data-id="element-390">3. Arrival Time:</strong> Please arrive
                                15 minutes before Iftar time. Late arrivals may
                                result in table reassignment.
                              </p>}
                            <p data-id="element-391">
                              <strong data-id="element-392">4. Dietary Requirements:</strong> We will
                              do our best to accommodate dietary needs, but
                              cannot guarantee allergen-free preparation.
                            </p>
                            <p data-id="element-393">
                              <strong data-id="element-394">5. Payment:</strong> All prices include
                              GST and service charges. Additional charges may
                              apply for special requests.
                            </p>
                          </div>
                          <label className="flex items-start gap-3 cursor-pointer" data-id="element-395">
                            <input type="checkbox" checked={acceptedTerms} onChange={e => setAcceptedTerms(e.target.checked)} className="mt-1 w-5 h-5 text-red-600 border-2 border-gray-300 rounded focus:ring-2 focus:ring-red-500" data-id="element-396" />
                            <span className="text-sm text-gray-700" data-id="element-397">
                              I have read and agree to the{' '}
                              <Link to="/terms" className="text-red-600 hover:text-red-700 font-semibold underline" data-id="element-398">
                                Terms & Conditions
                              </Link>{' '}
                              and{' '}
                              <Link to="/cancellation" className="text-red-600 hover:text-red-700 font-semibold underline" data-id="element-399">
                                Cancellation Policy
                              </Link>
                            </span>
                          </label>
                        </div>
                        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border-2 border-purple-200" data-id="element-400">
                          <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-401">
                            <CreditCardIcon className="w-5 h-5 text-purple-600" data-id="element-402" />
                            Payment Information
                          </h3>
                          <div className="space-y-3" data-id="element-403">
                            <p className="text-sm text-gray-700" data-id="element-404">
                              You will be redirected to our secure payment
                              gateway to complete your booking.
                            </p>
                            <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-405">
                              <ShieldCheckIcon className="w-4 h-4 text-green-600" data-id="element-406" />
                              <span data-id="element-407">Secure payment powered by BML</span>
                            </div>
                            <div className="bg-white rounded-xl p-4 mt-3" data-id="element-408">
                              <p className="text-xs text-gray-600 mb-2" data-id="element-409">
                                Accepted payment methods:
                              </p>
                              <div className="flex gap-2" data-id="element-410">
                                <div className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-xs font-semibold" data-id="element-411">
                                  Visa
                                </div>
                                <div className="px-3 py-1 bg-orange-100 text-orange-700 rounded text-xs font-semibold" data-id="element-412">
                                  Mastercard
                                </div>
                                <div className="px-3 py-1 bg-purple-100 text-purple-700 rounded text-xs font-semibold" data-id="element-413">
                                  Amex
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </>}
                    {/* Terms acceptance */}
                    <div className="bg-white rounded-xl p-4 border-2 border-gray-200" data-id="element-414">
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-415">
                        <input type="checkbox" checked={acceptedTerms} onChange={e => setAcceptedTerms(e.target.checked)} className="mt-1 w-5 h-5 text-red-600 border-2 border-gray-300 rounded focus:ring-2 focus:ring-red-500" data-id="element-416" />
                        <span className="text-sm text-gray-700" data-id="element-417">
                          I have read and agree to the{' '}
                          <Link to="/terms" className="text-red-600 hover:text-red-700 font-semibold underline" data-id="element-418">
                            Terms & Conditions
                          </Link>{' '}
                          and{' '}
                          <Link to="/cancellation" className="text-red-600 hover:text-red-700 font-semibold underline" data-id="element-419">
                            Cancellation Policy
                          </Link>
                        </span>
                      </label>
                    </div>
                  </div>}
                <div className="flex items-center justify-between mt-10 pt-6 border-t-2 border-gray-200" data-id="element-420">
                  {step > 1 && <button type="button" onClick={() => setStep(step - 1)} className="px-8 py-3 border-2 border-gray-300 rounded-xl text-gray-700 hover:border-red-500 hover:text-red-600 font-semibold transition-all" data-id="element-421">
                      Previous
                    </button>}
                  <button type="submit" disabled={step === 1 && (!availabilityChecked || !selectedTimeSlot) || step === 3 && !acceptedTerms} className="ml-auto bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-10 py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-422">
                    {step === 3 ? bookingType === 'promotional' ? 'Proceed to Payment' : 'Confirm Booking' : 'Continue'}
                  </button>
                </div>
              </form>
            </div>
          </div>
          {/* Summary Sidebar */}
          <div className="lg:col-span-1" data-id="element-423">
            <div className="bg-white rounded-3xl shadow-xl p-6 sticky top-8" data-id="element-424">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2" data-id="element-425">
                <SparklesIcon className="w-5 h-5 text-red-600" data-id="element-426" />
                Booking Summary
              </h3>
              <div className="space-y-4 mb-6" data-id="element-427">
                <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-xl p-4 border border-red-200" data-id="element-428">
                  <p className="text-sm text-gray-600 mb-1" data-id="element-429">Restaurant</p>
                  <p className="font-bold text-gray-900" data-id="element-430">The Spice Garden</p>
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-4 border border-blue-200" data-id="element-431">
                  <p className="text-sm text-gray-600 mb-1" data-id="element-432">Booking Type</p>
                  <p className="font-bold text-gray-900" data-id="element-433">
                    {bookingType === 'promotional' ? 'Ramadan Iftar Special' : 'Regular Table'}
                  </p>
                </div>
                {formData.date && <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-4 border border-orange-200" data-id="element-434">
                    <p className="text-sm text-gray-600 mb-1" data-id="element-435">Date</p>
                    <p className="font-bold text-gray-900" data-id="element-436">
                      {formatDateDisplay(formData.date)}
                    </p>
                  </div>}
                {selectedTimeSlot && <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-200" data-id="element-437">
                    <p className="text-sm text-gray-600 mb-1" data-id="element-438">Time</p>
                    <p className="font-bold text-gray-900" data-id="element-439">
                      {selectedTimeSlot}
                    </p>
                  </div>}
                <div className="bg-gradient-to-br from-amber-50 to-red-50 rounded-xl p-4 border border-amber-200" data-id="element-440">
                  <p className="text-sm text-gray-600 mb-1" data-id="element-441">Guests</p>
                  <p className="font-bold text-gray-900" data-id="element-442">
                    {formData.pax} people
                  </p>
                </div>
              </div>
              {bookingType === 'promotional' ? <>
                  {/* Coupon Code Section - Only for promotional */}
                  <div className="mb-6" data-id="element-443">
                    <label className="block text-sm font-bold text-gray-900 mb-2" data-id="element-444">
                      Have a coupon code?
                    </label>
                    {!appliedCoupon ? <div className="flex gap-2" data-id="element-445">
                        <input type="text" value={couponCode} onChange={e => {
                    setCouponCode(e.target.value.toUpperCase());
                    setCouponError('');
                  }} placeholder="Enter code" className="flex-1 min-w-0 px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm uppercase" data-id="element-446" />
                        <button type="button" onClick={applyCoupon} className="flex-shrink-0 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold text-sm transition-colors" data-id="element-447">
                          Apply
                        </button>
                      </div> : <div className="bg-green-50 border-2 border-green-300 rounded-lg p-3" data-id="element-448">
                        <div className="flex items-center justify-between" data-id="element-449">
                          <div data-id="element-450">
                            <p className="font-bold text-green-900" data-id="element-451">
                              {appliedCoupon.code}
                            </p>
                            <p className="text-xs text-green-700" data-id="element-452">
                              {appliedCoupon.type === 'percentage' ? `${appliedCoupon.discount}% off` : `MVR ${appliedCoupon.discount} off`}
                            </p>
                          </div>
                          <button type="button" onClick={removeCoupon} className="text-red-600 hover:text-red-700 text-sm font-semibold" data-id="element-453">
                            Remove
                          </button>
                        </div>
                      </div>}
                    {couponError && <p className="text-red-600 text-xs mt-1" data-id="element-454">{couponError}</p>}
                  </div>
                  {/* Price Breakdown - Only for promotional */}
                  <div className="border-t-2 border-gray-200 pt-6 space-y-3 text-sm" data-id="element-455">
                    <div className="flex justify-between" data-id="element-456">
                      <span className="text-gray-600" data-id="element-457">
                        Subtotal ({formData.pax} × MVR {pricePerPax})
                      </span>
                      <span className="font-semibold text-gray-900" data-id="element-458">
                        MVR {subtotal.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-459">
                      <span className="text-gray-600" data-id="element-460">GST (8%)</span>
                      <span className="font-semibold text-gray-900" data-id="element-461">
                        MVR {gstAmount.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-462">
                      <span className="text-gray-600" data-id="element-463">
                        Service Charge (10%)
                      </span>
                      <span className="font-semibold text-gray-900" data-id="element-464">
                        MVR {serviceChargeAmount.toFixed(2)}
                      </span>
                    </div>
                    {appliedCoupon && <div className="flex justify-between text-green-600" data-id="element-465">
                        <span className="font-semibold" data-id="element-466">
                          Discount ({appliedCoupon.code})
                        </span>
                        <span className="font-semibold" data-id="element-467">
                          - MVR {discountAmount.toFixed(2)}
                        </span>
                      </div>}
                    <div className="flex justify-between pt-4 border-t-2 border-gray-200" data-id="element-468">
                      <span className="font-bold text-gray-900 text-lg" data-id="element-469">
                        Total Amount
                      </span>
                      <span className="font-bold text-2xl bg-gradient-to-r from-red-600 to-red-700 bg-clip-text text-transparent" data-id="element-470">
                        MVR {total.toFixed(2)}
                      </span>
                    </div>
                  </div>
                  <div className="mt-6 bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-4" data-id="element-471">
                    <p className="text-sm text-purple-800 font-semibold text-center flex items-center justify-center gap-2" data-id="element-472">
                      <SparklesIcon className="w-4 h-4" data-id="element-473" />
                      Includes entry to lucky draw!
                    </p>
                  </div>
                </> : <>
                  {/* Card Guarantee Info - Only for regular */}
                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-300 rounded-xl p-6" data-id="element-474">
                    <div className="flex items-start gap-3 mb-4" data-id="element-475">
                      <ShieldCheckIcon className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" data-id="element-476" />
                      <div data-id="element-477">
                        <p className="font-bold text-blue-900 mb-2" data-id="element-478">
                          Card Guarantee Only
                        </p>
                        <p className="text-sm text-blue-800 mb-3" data-id="element-479">
                          Your card will NOT be charged now. Payment will be
                          made at the restaurant after your meal.
                        </p>
                        <p className="text-xs text-blue-700" data-id="element-480">
                          A card is required to secure your reservation. It will
                          only be charged if you fail to show up or cancel
                          within 24 hours.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 bg-white border-2 border-gray-200 rounded-xl p-4" data-id="element-481">
                    <h4 className="font-bold text-gray-900 mb-3 text-sm" data-id="element-482">
                      What to expect:
                    </h4>
                    <ul className="space-y-2 text-sm text-gray-700" data-id="element-483">
                      <li className="flex items-start gap-2" data-id="element-484">
                        <span className="text-green-600 mt-1" data-id="element-485">✓</span>
                        <span data-id="element-486">
                          Browse and order from our full à la carte menu
                        </span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-487">
                        <span className="text-green-600 mt-1" data-id="element-488">✓</span>
                        <span data-id="element-489">Pay only for what you order</span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-490">
                        <span className="text-green-600 mt-1" data-id="element-491">✓</span>
                        <span data-id="element-492">Table reserved for 2 hours</span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-493">
                        <span className="text-green-600 mt-1" data-id="element-494">✓</span>
                        <span data-id="element-495">Flexible arrival time</span>
                      </li>
                    </ul>
                  </div>
                </>}
            </div>
          </div>
        </div>
      </div>
      <Footer data-id="element-496" />
    </div>;
}